import { Button, Card, Col, EditButton, Form, getDefaultSortOrder, Icons, Input, List, Row, Select, Space, Table, TextField, useTable } from "@pankod/refine-antd";
import { HttpError, IResourceComponentsProps, LogicalFilter, OpenNotificationParams } from "@pankod/refine-core";
import { useInfiniteSelect } from "hooks/useInfiniteSelect";
import { IHealthInsurer, IParametrizableContent } from "interfaces";
import { IParametrizableContentFilter } from "interfaces/parametrizable-content";
import { debounce } from "lodash";
import { useMemo, useState } from "react";
import { CheckCircleFilled, MinusCircleOutlined } from '@ant-design/icons';

export const CustomizableContentList: React.FC<IResourceComponentsProps> = () => {
    const [inputValue, setInputValue] = useState("");
    const friendlyApiErrorMessage: false | OpenNotificationParams | ((error: unknown) => OpenNotificationParams) = {
        message: "Por favor, tente novamente.",
        description: "Ocorreu algum erro inesperado.",
        type: "error",
    }

    const { selectProps: healthInsurerSelectProps } = useInfiniteSelect<IHealthInsurer>({
        resource: "v1/health-insurer",
        optionLabel: "Description",
        optionValue: "HealthInsurerId",
        sort: [{ field: "Description", order: "asc" }],
        pageSize: 20,
        errorNotification: friendlyApiErrorMessage
    });

    const healthInsurerOptions = useMemo(() => [
        { label: "Todas", value: "" },
        ...(healthInsurerSelectProps.options ?? [])
    ], [healthInsurerSelectProps.options]);

    const debouncedSearch = useMemo(() => debounce(() => {
        searchFormProps.form?.submit();
    }, 300),
        []
    );

    const { tableProps, sorter, searchFormProps } = useTable<
        IParametrizableContent,
        HttpError,
        IParametrizableContentFilter
    >({
        resource: "v1/parametrizable-content/sites",
        initialCurrent: 1,
        initialPageSize: 20,
        hasPagination: true,
        onSearch: (values) => {
            const filtros: LogicalFilter[] = Object.entries(values).map(
                ([key, value]) => ({
                    field: key,
                    operator: "eq",
                    value,
                })
            );
            return filtros;
        },
        errorNotification: () => {
            return friendlyApiErrorMessage;
        },
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
        debouncedSearch();
    };

    const handleSelectOptionChange = (value: string) => {
        if (value === "") {
            handleCleanFilters();
        } else {
            searchFormProps.form?.submit();
        }
    };

    const handleFilterSelectOptions = (input: string, option?: { label?: unknown }): boolean => {
        return String(option?.label ?? "").toLowerCase().includes(input.toLowerCase());
    };

    const handleCleanFilters = () => {
        searchFormProps.form?.resetFields();
        searchFormProps.form?.submit();
    };

    return (
        <List
            canCreate={false}
            title="Conteúdo Parametrizável"
        >
            <Card title="Filtrar">
                <Form
                    {...searchFormProps}
                    layout="vertical"
                >
                    <Row gutter={[16, 16]} style={{ width: "100%" }}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="search" label="Buscar">
                                <Input
                                    name="search"
                                    placeholder="Digite um site"
                                    value={inputValue}
                                    onChange={handleInputChange}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="operator_id" label="Operadora">
                                <Select
                                    {...healthInsurerSelectProps}
                                    options={healthInsurerOptions}
                                    placeholder="Selecione uma operadora"
                                    onChange={handleSelectOptionChange}
                                    filterOption={handleFilterSelectOptions}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} style={{ textAlign: "right", marginTop: 8 }}>
                            <Space>
                                <Button
                                    type="default"
                                    icon={<Icons.ClearOutlined />}
                                    onClick={handleCleanFilters}
                                >
                                    Limpar
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<Icons.SearchOutlined />}
                                    htmlType="submit"
                                >
                                    Buscar
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </Card>
            <Table
                {...tableProps}
                rowKey="site_id"
            >
                <Table.Column
                    dataIndex="name"
                    title="Site"
                    defaultSortOrder={getDefaultSortOrder("name", sorter)}
                    sorter
                    render={(value) => (
                        <TextField
                            value={value || "-"}
                            style={{
                                wordBreak: "break-all",
                                textWrap: "wrap",
                                maxWidth: "100%",
                            }}
                        />
                    )}
                />
                <Table.Column
                    dataIndex="operator_description"
                    title="Operadora"
                    defaultSortOrder={getDefaultSortOrder("operator_description", sorter)}
                    sorter
                    render={(value) => (
                        <TextField
                            value={value || "-"}
                            style={{
                                wordBreak: "break-all",
                                textWrap: "wrap",
                                maxWidth: "100%",
                            }}
                        />
                    )}
                />
                <Table.Column
                    dataIndex="url"
                    title="URL"
                    defaultSortOrder={getDefaultSortOrder("url", sorter)}
                    sorter
                    render={(value) => (
                        <TextField
                            value={value || "-"}
                            style={{
                                wordBreak: "break-all",
                                textWrap: "wrap",
                                maxWidth: "100%",
                            }}
                        />
                    )}
                />
                <Table.Column
                    dataIndex="is_default"
                    title="Padrão"
                    defaultSortOrder={getDefaultSortOrder("is_default", sorter)}
                    sorter
                    align="center"
                    render={(value) => (value ?
                        <CheckCircleFilled
                            style={{ fontSize: '18px', color: '#2c9002' }}
                        />
                        :
                        <MinusCircleOutlined
                            style={{ fontSize: '18px', color: '#9a9a9a' }}
                        />)}
                />
                <Table.Column<IParametrizableContent>
                    dataIndex="actions"
                    title="Ações"
                    render={(_, record) => (
                        <Space>
                            <EditButton
                                style={{
                                    borderColor: "#1669B6",
                                    color: "#1669B6",
                                    padding: "2px 5px",
                                    height: "auto"
                                }}
                                recordItemId={record.site_id}
                            >
                                Editar site
                            </EditButton>
                        </Space>
                    )}
                />
            </Table>
        </List>
    )
}